import type { ActivePower, Combat } from '../core/combat';
import type { TurnView } from '../core/combat';
import type { Intent } from '../core/intent';
import type { RelicDef } from '../core/relic';
import type { GemWeights } from '../core/gempool';

export interface PresenterEnemy {
  readonly uid: number;
  readonly name: string;
  readonly hp: number;
  readonly maxHp: number;
  readonly block: number;
  readonly poison: number;
  /** The intent to draw. During a beat this is the one being performed. */
  readonly intent: Intent;
  /** True while this enemy's beat is the one on screen. */
  readonly acting: boolean;
}

/**
 * One shape for everything the screen draws, whether it is coming from live
 * combat or from a frozen beat.
 *
 * This exists because the renderer must not read `combat` directly during
 * playback — combat has already advanced to next turn, and a direct read
 * renders the future. Routing every draw call through here means the timing
 * rule is enforced in exactly one place, instead of being special-cased per
 * panel.
 */
export interface Presenter {
  readonly playerHp: number;
  readonly playerMaxHp: number;
  readonly playerBlock: number;
  readonly playerPoison: number;
  readonly playerEnergy: number;
  readonly turn: number;
  readonly phase: Combat['phase'];
  readonly enemies: readonly PresenterEnemy[];
  /** False during the enemy phase — the grip is already in the pile. */
  readonly showHand: boolean;
  readonly drawPile: number;
  readonly discardPile: number;
  readonly relics: readonly RelicDef[];
  readonly powers: readonly ActivePower[];
  readonly targetIndex: number;
  readonly voltRemainder: number;
  readonly freeSwapUsed: boolean;
  /** The run's gem composition. Cuts reshape this; the strip shows it. */
  readonly gemWeights: GemWeights;
}

/**
 * Build the presenter. With no `view` this is a straight read of live combat;
 * with one, the snapshotted fields win and everything that cannot change
 * mid-enemy-phase (piles, relics, powers) still comes from live state.
 */
export function present(
  combat: Combat,
  view: TurnView | undefined,
  actingEnemyUid: number | null,
): Presenter {
  const enemies: PresenterEnemy[] = (view?.enemies ?? combat.enemies).map(e => ({
    uid: e.uid,
    name: 'name' in e ? e.name : e.def.name,
    hp: e.hp,
    maxHp: e.maxHp,
    block: e.block,
    poison: e.poison,
    intent: e.intent,
    acting: actingEnemyUid !== null && e.uid === actingEnemyUid,
  }));

  return {
    playerHp: view?.playerHp ?? combat.player.hp,
    playerMaxHp: view?.playerMaxHp ?? combat.player.maxHp,
    playerBlock: view?.playerBlock ?? combat.player.block,
    playerPoison: view?.playerPoison ?? combat.player.poison,
    playerEnergy: view?.playerEnergy ?? combat.player.energy,
    turn: view?.turn ?? combat.turn,
    phase: view?.phase ?? combat.phase,
    enemies,
    showHand: view ? view.showHand : true,
    drawPile: combat.drawPile.length,
    discardPile: combat.discardPile.length,
    relics: combat.relics,
    powers: combat.powers,
    targetIndex: combat.targetIndex,
    voltRemainder: combat.voltRemainder,
    freeSwapUsed: combat.freeSwapUsed,
    gemWeights: combat.gemWeights,
  };
}
